import { useEffect, useRef, useState } from 'react'
import { Route, Switch, useParams } from '#router'
import { AdminDashboard } from './admin-dashboard'
import { AuditionsHub } from './auditions'
import { AuditionArchive } from './auditions/archive'
import { AuditionDetail } from './auditions/detail'
import { CatalogHealthPage } from './catalog'
import { ClientDetail } from './coaching/client-detail'
import { ClientList } from './coaching/client-list'
import { CoachingDashboard } from './coaching/coaching-dashboard'
import {
  useCoachingClient,
  useCoachingClients,
} from './coaching/hooks/use-coaching-clients'
import { Tabs } from './coaching/tabs'
import { CompaniesList } from './companies/companies-list'
import { ProblemsCommandPalette } from './problems/command-palette'
import { ProblemEditor } from './problems/problem-editor'
import { ProblemsList } from './problems/problems-list'
import { SourceContentDetail } from './sources/source-content-detail'
import { SourceEditor } from './sources/source-editor'
import { SourcesList } from './sources/sources-list'
import { SprintPage } from './sprints/sprint-page'
import { SprintsList } from './sprints/sprints-list'
import { SystemDesignEditor } from './system-design/system-design-editor'
import { SystemDesignTable } from './system-design/system-design-table'
import { TasksAdmin } from './tasks/tasks-admin'
import { AllUsersTab } from './users/all-users-tab'

function ClientListView() {
  const { clients, loading, error, addClient, editClient, refresh } =
    useCoachingClients()

  return (
    <ClientList
      clients={clients}
      loading={loading}
      error={error}
      onAddClient={addClient}
      onEditClient={editClient}
      onRefresh={refresh}
    />
  )
}

/**
 * Coaching hub: top-level tabs over the direct-coaching clients (default) and
 * the full platform-user roster, where freemium users can be promoted into
 * clients. Promote reuses the Add Client flow (see AllUsersTab).
 */
function CoachingHub() {
  const [view, setView] = useState<'clients' | 'users'>('clients')

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <div className="mb-5">
        <Tabs
          tabs={[
            { id: 'clients', label: 'Direct-Coach Clients' },
            { id: 'users', label: 'All Users' },
          ]}
          activeTab={view}
          onTabChange={(id) => setView(id as 'clients' | 'users')}
        />
      </div>
      {view === 'clients' ? <ClientListView /> : <AllUsersTab />}
    </div>
  )
}

type CoachingView = 'classic' | 'v2'

function requestedCoachingView(): CoachingView | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('coachingView') ===
    'v2'
    ? 'v2'
    : null
}

function clearRequestedCoachingView() {
  if (typeof window === 'undefined') return
  const destination = new URL(window.location.href)
  if (!destination.searchParams.has('coachingView')) return
  destination.searchParams.delete('coachingView')
  window.history.replaceState(
    window.history.state,
    '',
    `${destination.pathname}${destination.search}${destination.hash}`,
  )
}

export function ClientDetailPage() {
  const params = useParams()
  const {
    client,
    loading,
    error,
    refresh,
    update,
    assignHomework,
    removeHomework,
    toggleDoneBefore,
    updateLastAttempt,
    checkIn,
  } = useCoachingClient(params.id)
  const serverView: CoachingView =
    client?.dashboardExperience === 'v2' ? 'v2' : 'classic'
  const previousServerView = useRef(serverView)
  const [previewView, setPreviewView] = useState<CoachingView | null>(
    requestedCoachingView,
  )
  const view = previewView ?? serverView

  // The persisted rollout is authoritative. A manual/query preview is local
  // to this mounted page and is discarded as soon as fresh server state moves
  // the client between experiences.
  useEffect(() => {
    if (previousServerView.current === serverView) return
    previousServerView.current = serverView
    setPreviewView(null)
  }, [serverView])

  const handleViewChange = (id: string) => {
    const next: CoachingView = id === 'v2' ? 'v2' : 'classic'
    setPreviewView(next)
    clearRequestedCoachingView()
  }

  return (
    <>
      <div className="border-border bg-background border-b px-6 py-3">
        <Tabs
          tabs={[
            { id: 'classic', label: 'Classic' },
            { id: 'v2', label: 'Coaching v2' },
          ]}
          activeTab={view}
          onTabChange={handleViewChange}
        />
      </div>
      {view === 'classic' ? (
        <ClientDetail
          client={client}
          loading={loading}
          error={error}
          onUpdate={update}
          onAssignHomework={assignHomework}
          onRemoveHomework={removeHomework}
          onToggleDoneBefore={toggleDoneBefore}
          onUpdateLastAttempt={updateLastAttempt}
          onRefresh={refresh}
          onCheckIn={checkIn}
        />
      ) : loading ? (
        <div className="text-muted-foreground flex min-h-[50vh] items-center justify-center">
          Loading…
        </div>
      ) : error || !client ? (
        // Error wins over a stale cached client, matching Classic
        // (client-detail.tsx) so a failed fetch is never silently masked.
        <div className="text-destructive flex min-h-[50vh] items-center justify-center">
          {error ?? 'Client not found'}
        </div>
      ) : (
        <CoachingDashboard
          client={client}
          clientId={client.id}
          showRolloutStatus
        />
      )}
    </>
  )
}

function CompaniesListPage() {
  return <CompaniesList />
}

function SourcesListPage() {
  return <SourcesList />
}

function SourceEditorPage() {
  return <SourceEditor />
}

function SourceContentDetailPage() {
  return <SourceContentDetail />
}

function ProblemsListPage() {
  return (
    <ProblemsCommandPalette>
      <ProblemsList />
    </ProblemsCommandPalette>
  )
}

function ProblemEditorPage() {
  return (
    <ProblemsCommandPalette>
      <ProblemEditor />
    </ProblemsCommandPalette>
  )
}

function SystemDesignListPage() {
  return <SystemDesignTable />
}

function SystemDesignEditorPage() {
  return <SystemDesignEditor />
}

function AuditionDetailPage() {
  const params = useParams()
  // RouteParams values are optional; asserting non-null here sent
  // `submissionId: undefined` into a uuid-validated query and parked the
  // page on its loading state forever.
  if (!params.id) return null
  return <AuditionDetail submissionId={params.id} />
}

function SprintDetailPage() {
  const params = useParams()
  if (!params.id) return null
  return <SprintPage sprintId={params.id} />
}

export function Admin() {
  return (
    <div className="bg-background min-h-screen">
      <Switch>
        {/* Client routes */}
        <Route path="/admin/coaching/client/:id">{ClientDetailPage}</Route>
        <Route path="/admin/coaching">{CoachingHub}</Route>

        {/* Audition funnel routes */}
        {/* Above the detail route: `/admin/auditions/:id` matches the bare
            `/admin/auditions/archive` with id = "archive". */}
        <Route path="/admin/auditions/archive/:group?">{AuditionArchive}</Route>
        <Route path="/admin/auditions/:id">{AuditionDetailPage}</Route>
        <Route path="/admin/auditions">{AuditionsHub}</Route>

        {/* Problems routes (top-level) */}
        <Route path="/admin/problems/:id">{ProblemEditorPage}</Route>
        <Route path="/admin/problems">{ProblemsListPage}</Route>

        {/* Companies route (top-level) */}
        <Route path="/admin/companies">{CompaniesListPage}</Route>

        {/* Sources routes */}
        <Route path="/admin/sources/:sourceId/content/:contentId">
          {SourceContentDetailPage}
        </Route>
        <Route path="/admin/sources/:id">{SourceEditorPage}</Route>
        <Route path="/admin/sources">{SourcesListPage}</Route>

        {/* System Design routes */}
        <Route path="/admin/system-design/:id">{SystemDesignEditorPage}</Route>
        <Route path="/admin/system-design">{SystemDesignListPage}</Route>

        {/* Tasks route */}
        <Route path="/admin/tasks">{TasksAdmin}</Route>

        {/* Sprint calendar and the admin exceptions (LG-1004) */}
        <Route path="/admin/sprints/:id">{SprintDetailPage}</Route>
        <Route path="/admin/sprints">{SprintsList}</Route>

        {/* Stripe catalog health (LG-433) */}
        <Route path="/admin/catalog">{CatalogHealthPage}</Route>

        {/* Admin dashboard landing page */}
        <Route path="/admin">{AdminDashboard}</Route>
      </Switch>
    </div>
  )
}
